import { useState, useRef, useEffect } from 'react'
import { Search, X, ChevronDown } from 'lucide-react'
import clsx from 'clsx'

interface Option {
  value: string
  label: string
}

interface MultiSelectProps {
  label?: string
  options: Option[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  searchable?: boolean
  lazy?: boolean
  onOpen?: () => void
  className?: string
}

export default function MultiSelect({ label, options, selected, onChange, placeholder = 'Selecione', searchable = true, lazy = false, onOpen, className }: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function handleOpen() {
    if (!open && lazy && onOpen) onOpen()
    setOpen(!open)
    setSearch('')
  }

  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  function clear() {
    onChange([])
  }

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options

  const selectedLabels = selected.map(v => options.find(o => o.value === v)?.label).filter(Boolean)

  return (
    <div ref={ref} className={clsx('relative', className)}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>}

      <button
        type="button"
        onClick={handleOpen}
        className="h-11 w-full flex items-center justify-between rounded-lg border border-gray-300 px-4 text-sm shadow-xs transition-colors focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none bg-white"
      >
        <span className={clsx('truncate text-left flex-1', selected.length === 0 && 'text-gray-400')}>
          {selected.length === 0
            ? placeholder
            : selected.length === 1
              ? selectedLabels[0]
              : `${selected.length} selecionados`
          }
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
          {selected.length > 0 && (
            <span onClick={e => { e.stopPropagation(); clear() }} className="text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={clsx('w-4 h-4 text-gray-400 transition-transform', open && 'rotate-180')} />
        </div>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 flex flex-col animate-fade-in">
          {searchable && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar..."
                  autoFocus
                  className="w-full h-9 pl-8 pr-3 rounded-lg border border-gray-200 text-sm placeholder:text-gray-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-500/10 outline-none"
                />
              </div>
            </div>
          )}

          <div className="overflow-y-auto flex-1 p-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-3">Nenhum resultado</p>
            ) : (
              filtered.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left hover:bg-gray-50 transition-colors"
                >
                  <div className={clsx(
                    'w-4 h-4 rounded border-[1.5px] flex items-center justify-center flex-shrink-0 transition-colors',
                    selected.includes(option.value)
                      ? 'bg-brand-500 border-brand-500'
                      : 'border-gray-300'
                  )}>
                    {selected.includes(option.value) && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </div>
                  <span className="text-gray-700">{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
